import React, { useState, FC, useEffect, useContext, Fragment } from 'react';
import { Box, Button, Grid } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { Action, ActionSettings } from 'models/action';
import { Category, SubCategory } from 'models/organization';
import { MaterialGroup } from 'models/organization';
import { useTranslation } from 'react-i18next';
import ActionLeftSection from './action-left-section.component';
import ActionRightSection from './action-right-section.component';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { RestorationType } from 'models/order';

type AddActionSectionDialogProps = {
    open: boolean;
    onClose: () => void;
    action: any | null;
    savedActions: Action[];
    categories: Category[];
    materials: MaterialGroup[];
    onSave: () => void;
    onCreate: (action: Action) => Promise<void>;
    onDelete: (actionId: string) => Promise<void>;
};

const AddActionSectionDialog: FC<AddActionSectionDialogProps> = ({
    open,
    onClose,
    action: initialAction,
    savedActions,
    categories,
    materials,
    onSave,
    onCreate,
    onDelete,
}) => {
    const { t: translate } = useTranslation(['common', 'orders', 'manage-actions']);
    const { setAppToasterContent } = useContext(AppToasterContext);
    const [action, setAction] = useState({} as any);
    const [selectedSubCategories, setSelectedSubCategories] = useState<SubCategory[]>([]);
    const [subCategoriesForAction, setSubCategoriesForAction] = useState<SubCategory[]>([]);
    const [subCategoryMenuAnchor, setSubCategoryMenuAnchor] = useState<null | HTMLElement>(null);

    // Initialize action when modal opens or initialAction changes
    useEffect(() => {
        if (initialAction) {
            setAction({ ...initialAction });

            // Handle subcategories
            if (initialAction.mainCategory) {
                const category = categories.find((cat) => cat._id === initialAction.mainCategory._id);
                if (category) {
                    const subCats = category.subCategories.map((sub) => ({ ...sub, selected: false }));
                    setSubCategoriesForAction(subCats);

                    if (initialAction.subCategory?.length > 0) {
                        const selected = subCats.filter((sub) =>
                            initialAction.subCategory.some(
                                (sel: SubCategory) => String(sel._id ?? sel) === String(sub._id),
                            ),
                        );
                        selected.forEach((sub) => (sub.selected = true));
                        setSelectedSubCategories(selected);
                    }
                }
            }
        } else {
            // Reset to empty action
            setAction({} as Action);
            setSelectedSubCategories([]);
            setSubCategoriesForAction([]);
        }
    }, [initialAction, categories, open]);

    const handleInputChange = (field: keyof Action, value: any) => {
        if (field === 'materialGroup') {
            const selectedMaterial = materials.find((material) => material._id === value);
            if (selectedMaterial) {
                setAction((prev: Action) => ({
                    ...prev,
                    materialGroup: selectedMaterial,
                    settings: {
                        ...prev.settings,
                        material: {
                            ...(prev.settings?.material ?? {}),
                            include: true,
                        },
                    },
                }));
            } else {
                setAction((prev: Action) => ({
                    ...prev,
                    materialGroup: null,
                    settings: {
                        ...prev.settings,
                        material: {
                            ...(prev.settings?.material ?? {}),
                            include: false,
                        },
                    },
                }));
            }
            return;
        }
        setAction((prev: Action) => ({ ...prev, [field]: value }));
    };

    const handleSettingsChange = (category: keyof ActionSettings, field: string, value: any) => {
        setAction((prev: Action) => ({
            ...prev,
            settings: {
                ...prev.settings,
                [category]: {
                    ...(prev.settings?.[category] ?? {}),
                    [field]: value,
                },
            },
        }));
    };

    const handleMainCategoryChange = (categoryId: string) => {
        const selectedCat = categories.find((cat) => cat._id === categoryId);
        if (selectedCat) {
            setAction((prev: any) => ({ ...prev, mainCategory: selectedCat, subCategory: [] }));
        }
        setSelectedSubCategories([]);

        const category = categories.find((cat) => cat._id === categoryId);
        if (category) {
            const subCats = category.subCategories.map((sub) => ({ ...sub, selected: false }));
            setSubCategoriesForAction(subCats);
        }
    };

    const handleSubCategoryChange = (subCategory: SubCategory, isSelected: boolean) => {
        const updatedSubCat = { ...subCategory, selected: isSelected };

        if (isSelected) {
            setSelectedSubCategories((prev) => [...prev, updatedSubCat]);
        } else {
            setSelectedSubCategories((prev) => prev.filter((sub) => sub._id !== subCategory._id));
        }

        setSubCategoriesForAction((prev) => prev.map((sub) => (sub._id === subCategory._id ? updatedSubCat : sub)));
    };

    const handleTeethOptionChange = (value: string) => {
        setAction((prev: any) => ({
            ...prev,
            teethOption: value,
            costPerTooth: value !== 'Single tooth' ? false : prev.costPerTooth,
        }));
    };

    const validateVat = (value: string) => {
        const reg = /^[0-9]*([0-9,.]{2,3})?$/;
        return reg.test(value) ? value : value.slice(0, -1);
    };

    const handleVatChange = (value: string) => {
        const validatedValue = validateVat(value);
        setAction((prev: any) => ({ ...prev, vat: validatedValue }));
    };

    const checkExistingAction = () => {
        return savedActions.some(
            (savedAction) =>
                action.name === savedAction.name &&
                action.number === savedAction.number &&
                savedAction._id !== action._id,
        );
    };

    const isFormValid = () => {
        // saveAction takes a non-null RestorationType (DD-3847)
        if (
            !action.number ||
            !action.name ||
            !action.mainCategory ||
            !action.denthubRestorationType ||
            selectedSubCategories.length === 0
        ) {
            return false;
        }

        if (action.settings?.numberOfUnits?.include || action.settings?.numberOfUnits?.mandatory) {
            return !!action.settings?.numberOfUnits.header;
        }

        return true;
    };

    const handleSave = async () => {
        try {
            if (checkExistingAction()) {
                handleError(translate('manage-actions:ACTION_EXISTS'));
                return;
            }

            const actionToSave = {
                ...action,
                subCategory: selectedSubCategories.map((sub) => sub._id),
                mainCategory: action.mainCategory?._id,
                materialGroup: action.materialGroup?._id,
                vat: action.vat ? parseFloat(action.vat.replace(',', '.')) : null,
            };

            await onCreate(actionToSave); // For both create and update
            handleSuccess();
            onSave();
            onClose();
        } catch (error) {
            handleError(translate('manage-actions:ERROR_SAVING_ACTION'));
        }
    };

    const handleDelete = async () => {
        if (action._id) {
            try {
                await onDelete(action._id);
                handleSuccess();
                onSave();
                onClose();
            } catch (error) {
                handleError(translate('manage-actions:ERROR_DELETING_ACTION'));
            }
        }
    };

    const handleSuccess = () => {
        setAppToasterContent({
            isOpen: true,
            severity: 'success',
            message: translate('common:CHANGES_SAVED'),
        });
    };

    const handleError = (message: string) => {
        setAppToasterContent({
            isOpen: true,
            severity: 'error',
            message: message,
        });
    };

    const handleDenthubRestorationTypeChange = (value: RestorationType) => {
        setAction((prev: any) => ({ ...prev, denthubRestorationType: value }));
    };

    return (
        <DHDialog
            fullWidth
            maxWidth="lg"
            open={open}
            onClose={onClose}
            title={action._id ? translate('orders:EDIT_ACTION') : translate('orders:ADD_ACTION')}
            actions={
                <Fragment>
                    {action._id && (
                        <Box mr={1}>
                            <Button onClick={handleDelete} color="error" startIcon={<DeleteIcon />}>
                                {translate('common:DELETE')}
                            </Button>
                        </Box>
                    )}
                    <Button onClick={handleSave} variant="contained" disabled={!isFormValid()}>
                        {translate('common:SAVE')}
                    </Button>
                </Fragment>
            }
        >
            <Grid container spacing={3}>
                <ActionLeftSection
                    action={action}
                    categories={categories}
                    materials={materials}
                    subCategoriesForAction={subCategoriesForAction}
                    selectedSubCategories={selectedSubCategories}
                    subCategoryMenuAnchor={subCategoryMenuAnchor}
                    setSubCategoryMenuAnchor={setSubCategoryMenuAnchor}
                    handleInputChange={handleInputChange}
                    handleMainCategoryChange={handleMainCategoryChange}
                    handleSubCategoryChange={handleSubCategoryChange}
                    handleTeethOptionChange={handleTeethOptionChange}
                    handleSettingsChange={handleSettingsChange}
                    handleVatChange={handleVatChange}
                    handleDenthubRestorationTypeChange={handleDenthubRestorationTypeChange}
                />
                <ActionRightSection action={action} handleSettingsChange={handleSettingsChange} />
            </Grid>
        </DHDialog>
    );
};

export default AddActionSectionDialog;
